import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  Alert,
  LinearProgress,
  Collapse
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  Search as SearchIcon,
  CheckCircle as CheckCircleIcon,
  Error as ErrorIcon,
  PriorityHigh as WarningIcon,
  CloudUpload as CloudUploadIcon,
  FolderOpen as FolderIcon,
  Label as LabelIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon
} from '@mui/icons-material';
import { format, formatDistanceToNow } from 'date-fns';
import AdminService from '../../services/AdminService';
import apiService from '../../services/api';

// Format file size for display
const formatFileSize = (sizeBytes) => {
  if (sizeBytes < 1024) {
    return `${sizeBytes} bytes`;
  } else if (sizeBytes < 1024 * 1024) {
    return `${(sizeBytes / 1024).toFixed(2)} KB`;
  } else if (sizeBytes < 1024 * 1024 * 1024) {
    return `${(sizeBytes / (1024 * 1024)).toFixed(2)} MB`;
  } else {
    return `${(sizeBytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  }
};

// File upload dialog component
const DataSourceUploadDialog = ({ open, onClose, onSuccess }) => {
  const [files, setFiles] = useState([]);
  const [overwrite, setOverwrite] = useState(false);
  const [confirmOverwrite, setConfirmOverwrite] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modifiedFiles, setModifiedFiles] = useState([]);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setFiles([]);
      setOverwrite(false);
      setConfirmOverwrite(false);
      setError(null);
      setSuccess(null);
      setUploadProgress(0);
      setModifiedFiles([]);
      setShowConfirmDialog(false);
    }
  }, [open]);

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    // Map files with screen transcript flag for MP4s
    const filesWithMetadata = selectedFiles.map(file => ({
      file: file,
      isMP4: file.name.toLowerCase().endsWith('.mp4'),
      screenTranscript: file.name.toLowerCase().endsWith('.mp4') // Default true for MP4s
    }));
    setFiles(filesWithMetadata);
  };

  const toggleScreenTranscript = (index) => {
    setFiles(prev => prev.map((item, i) => 
      i === index ? { ...item, screenTranscript: !item.screenTranscript } : item
    ));
  };

  // Handle file upload
  const handleUpload = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    setError(null);
    setSuccess(null);
    
    try {
      // Create FormData object for the API request
      const formData = new FormData();
      
      // Append each file to the FormData
      files.forEach(fileItem => {
        formData.append('files', fileItem.file);
      });
      
      // Add screen_transcript flag for MP4 files
      const hasMP4 = files.some(f => f.isMP4);
      if (hasMP4) {
        const mp4File = files.find(f => f.isMP4);
        formData.append('screen_transcript', mp4File.screenTranscript ? 'true' : 'false');
      }
      
      // Add overwrite flag (but not confirm_overwrite yet)
      formData.append('overwrite', overwrite.toString());
      formData.append('confirm_overwrite', 'false');
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 300);
      
      // Call the documents upload API endpoint
      const response = await apiService.uploadDocuments(formData);
      
      // Complete the progress bar
      setUploadProgress(100);
      clearInterval(progressInterval);
      
      // Check if the backend found modified files that need confirmation
      if (response.status === "content_changed" && response.modified_files) {
        // Show confirmation dialog
        setModifiedFiles(response.modified_files);
        setShowConfirmDialog(true);
        return;
      }
      
      // If no modified files, display success message
      if (response.message) {
        setSuccess(response.message);
        
        // If there are duplicate files, show additional details
        if (response.duplicates_skipped && response.duplicates_skipped.length > 0) {
          setSuccess(prev => `${prev}\nSkipped duplicates: ${response.duplicates_skipped.join(", ")}`);
        }
        
        if (response.duplicates_overwritten && response.duplicates_overwritten.length > 0) {
          setSuccess(prev => `${prev}\nOverwritten duplicates: ${response.duplicates_overwritten.join(", ")}`);
        }
        
        // Close the dialog after a delay to allow user to see the message
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 5000);
      }
    } catch (err) {
      console.error('Error uploading files:', err);
      setError(err.message || 'Failed to upload files. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  // Handle confirming the overwrite of modified files
  const handleConfirmOverwrite = async (confirmed) => {
    if (!confirmed) {
      setShowConfirmDialog(false);
      setModifiedFiles([]);
      return;
    }

    setUploading(true);
    setShowConfirmDialog(false);
    
    try {
      // Create FormData object for the API request with confirm_overwrite=true
      const formData = new FormData();
      
      // Append each file to the FormData
      files.forEach(file => {
        formData.append('files', file);
      });
      
      // Add overwrite and confirm_overwrite flags
      formData.append('overwrite', overwrite.toString());
      formData.append('confirm_overwrite', 'true');
      
      // Simulate upload progress
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 95) {
            clearInterval(progressInterval);
            return 95;
          }
          return prev + 5;
        });
      }, 300);
      
      // Call the documents upload API endpoint with confirm_overwrite set to true
      const response = await apiService.uploadDocuments(formData);
      
      // Complete the progress bar
      setUploadProgress(100);
      clearInterval(progressInterval);
      
      // Display a success message with details about overwritten files
      if (response.message) {
        setSuccess(response.message);
        
        // If there are duplicate files, show additional details
        if (response.duplicates_skipped && response.duplicates_skipped.length > 0) {
          setSuccess(prev => `${prev}\nSkipped duplicates: ${response.duplicates_skipped.join(", ")}`);
        }
        
        if (response.duplicates_overwritten && response.duplicates_overwritten.length > 0) {
          setSuccess(prev => `${prev}\nOverwritten duplicates: ${response.duplicates_overwritten.join(", ")}`);
        }
        
        // Close the dialog after a delay to allow user to see the message
        setTimeout(() => {
          onClose();
          if (onSuccess) onSuccess();
        }, 5000);
      }
    } catch (err) {
      console.error('Error uploading files with confirm overwrite:', err);
      setError(err.message || 'Failed to upload files. Please try again.');
      setUploadProgress(0);
    } finally {
      setUploading(false);
      setModifiedFiles([]);
    }
  };
  
  return (
    <Dialog 
      open={open} 
      onClose={!uploading ? onClose : undefined}
      maxWidth="md"
      fullWidth
    >      
      <DialogTitle>Upload Data Sources</DialogTitle>
      <DialogContent>
        <DialogContentText paragraph>
          Upload files to be processed and indexed for search. Supported formats include PDF, JSON, TXT, DOCX, HTML, MD and CHM.
          <br />
          <em>Note: Duplicate files with the same content will be detected even if filenames are different. If "Overwrite existing files" is checked, duplicates will be replaced; otherwise, they will be skipped.</em>
        </DialogContentText>
        
        {/* Confirmation dialog for modified files */}
        {showConfirmDialog && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" sx={{ mb: 1 }}>
              Modified File Content Detected
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              The following file(s) already exist with different content:
            </Typography>
            
            <Box component="ul" sx={{ pl: 2, mb: 2 }}>
              {modifiedFiles.map((file, index) => (
                <Box component="li" key={index} sx={{ mb: 0.5 }}>
                  <Typography variant="body2">
                    {file}
                  </Typography>
                </Box>
              ))}
            </Box>
            
            <Typography variant="body2" sx={{ mb: 2 }}>
              Do you want to overwrite these files with the new content?
            </Typography>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              <Button 
                size="small" 
                variant="outlined" 
                onClick={() => handleConfirmOverwrite(false)}
                disabled={uploading}
              >
                Cancel
              </Button>
              <Button 
                size="small" 
                variant="contained" 
                color="warning"
                onClick={() => handleConfirmOverwrite(true)}
                disabled={uploading}
              >
                Confirm Overwrite
              </Button>
            </Box>
          </Alert>
        )}
          
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}
        
        <Box sx={{ mb: 3 }}>
          <input
            type="file"
            id="file-upload"
            multiple
            onChange={handleFileChange}
            style={{ display: 'none' }}
            disabled={uploading}
          />
          <label htmlFor="file-upload">
            <Button
              variant="contained"
              component="span"
              startIcon={<CloudUploadIcon />}
              disabled={uploading}
            >
              Select Files
            </Button>
          </label>
          
          {files.length > 0 && (
            <Paper sx={{ mt: 2, p: 2, maxHeight: '200px', overflow: 'auto' }}>
              <Typography variant="subtitle2" gutterBottom>
                Selected Files:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {files.map((fileItem, index) => (
                  <Box component="li" key={index} sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Typography variant="body2">
                      {fileItem.file.name} ({formatFileSize(fileItem.file.size)})
                    </Typography>
                    {fileItem.isMP4 && (
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={fileItem.screenTranscript}
                            onChange={() => toggleScreenTranscript(index)}
                            disabled={uploading}
                          />
                        }
                        label={<Typography variant="caption">Screen Transcript</Typography>}
                        sx={{ ml: 1 }}
                      />
                    )}
                  </Box>
                ))}
              </Box>
            </Paper>
          )}
        </Box>
        
        <FormGroup>
          <FormControlLabel
            control={
              <Switch 
                checked={overwrite} 
                onChange={(e) => setOverwrite(e.target.checked)}
                disabled={uploading}
              />
            }
            label="Overwrite existing files"
          />
        </FormGroup>
        
        {uploading && (
          <Box sx={{ width: '100%', mt: 2 }}>
            <LinearProgress variant="determinate" value={uploadProgress} />
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 1 }}>
              {uploadProgress}% - Processing files...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button 
          onClick={onClose} 
          disabled={uploading}
        >
          Cancel
        </Button>
        <Button 
          onClick={handleUpload} 
          variant="contained" 
          color="primary"
          disabled={files.length === 0 || uploading}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const DataSources = () => {
  const [dataSources, setDataSources] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalSources, setTotalSources] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  
  // Field mapping based on API requirements
  const [fieldMapping, setFieldMapping] = useState({
    filename: "filename",
    type: "type", 
    pages: "page_count",
    chunks: "chunk_count"
  });
  
  // Filter states
  const [sourceType, setSourceType] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // Dialog states
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [deleteSourceId, setDeleteSourceId] = useState(null);
  // Add state for filename confirmation
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");
  const [deleteConfirmError, setDeleteConfirmError] = useState("");
  
  // Track expanded rows for inline details
  const [expandedRows, setExpandedRows] = useState({});

  // Add state for upload dialog
  const [openUploadDialog, setOpenUploadDialog] = useState(false);
  
  // Toggle row expansion
  const toggleRowExpansion = (sourceId) => {
    setExpandedRows(prev => ({
      ...prev,
      [sourceId]: !prev[sourceId]
    }));
  };
  
  // Fetch data sources from API
  const fetchDataSources = async () => {
    try {
      setRefreshing(true);
      setError(null);
      
      const filters = {
        page: page + 1, // API uses 1-based pagination
        limit: rowsPerPage,
        sort_by: sortBy,
        sort_direction: sortDirection
      };
      
      // Add source type filter if it exists
      if (sourceType) {
        filters.source_type = sourceType;
        console.log("Filtering by source type:", sourceType);
      }
      
      // Add search query if it exists
      if (searchQuery) {
        filters.search = searchQuery;
      }
      
      console.log("Fetching data sources with params:", filters);
      
      try {
        const response = await AdminService.getDataSources(filters);
        console.log("Data sources response:", response);
        console.log("Response sources array:", response?.sources);
        console.log("Response sources length:", response?.sources?.length);
        
        if (response && response.sources) {
          console.log("Received data sources:", response.sources.length);
          console.log("First source sample:", response.sources[0]);
          
          // Check if API returned paginated data or all data
          const allSources = response.sources;
          const totalCount = response.total_count || response.total || response.totalSources || allSources.length;
          
          // If API didn't paginate (returned all data), do client-side pagination
          if (allSources.length === totalCount || allSources.length > rowsPerPage) {
            console.log("API returned all data, implementing client-side pagination");
            const startIndex = page * rowsPerPage;
            const endIndex = startIndex + rowsPerPage;
            const paginatedData = allSources.slice(startIndex, endIndex);
            setDataSources(paginatedData);
            console.log(`Showing items ${startIndex + 1}-${Math.min(endIndex, totalCount)} of ${totalCount}`);
          } else {
            // API properly paginated the data
            console.log("API returned paginated data");
            setDataSources(allSources);
          }
          
          setTotalSources(totalCount);
          console.log("Total sources count:", totalCount);
          
          // If the API provides field mapping, use it
          if (response.field_mapping) {
            console.log("Using field mapping from API:", response.field_mapping);
            setFieldMapping(response.field_mapping);
          }
        } else {
          console.warn("No sources found in response or response is invalid:", response);
          setDataSources([]);
          setTotalSources(0);
        }
      } catch (apiError) {
        console.error('API error fetching data sources:', apiError);
        setError('Failed to load data sources from the server. Please try again later.');
        setDataSources([]);
        setTotalSources(0);
      }
    } catch (err) {
      console.error('Error fetching data sources:', err);
      setError('Failed to load data sources. Please try again later.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };
  
  // FIXED: Handle changes to pagination - refetch data when page or rowsPerPage changes
  const handleChangePage = (event, newPage) => {
    console.log("Changing page to:", newPage);
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    console.log("Changing rows per page to:", newRowsPerPage);
    setRowsPerPage(newRowsPerPage);
    setPage(0); // Reset to first page when changing page size
  };
  
  // Apply filters - force a refresh
  const handleApplyFilters = () => {
    if (page === 0) {
      // If already on page 0, manually trigger fetch
      fetchDataSources();
    } else {
      // Reset to first page - this will trigger the useEffect
      setPage(0);
    }
  };
  
  // Clear filters
  const handleClearFilters = () => {
    setSourceType('');
    setSearchQuery('');
    // Clearing filters will trigger the filter useEffect which resets page
  };
  
  // Sort by column
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to asc
      setSortBy(column);
      setSortDirection('asc');
    }
    
    setPage(0);
    // Don't call fetchDataSources here, let useEffect handle it
  };
  
  // Handle refresh button click
  const handleRefresh = () => {
    fetchDataSources();
  };
  
  // Open delete confirmation dialog
  const handleOpenDeleteDialog = (sourceId) => {
    setDeleteSourceId(sourceId);
    setDeleteConfirmInput("");
    setDeleteConfirmError("");
    setOpenDeleteDialog(true);
  };
  
  // Close delete confirmation dialog
  const handleCloseDeleteDialog = () => {
    setOpenDeleteDialog(false);
    setDeleteSourceId(null);
  };
  
  // Confirm delete
  const handleConfirmDelete = async () => {
    // Require filename confirmation
    const filename = getFieldValue(
      dataSources.find(ds => (ds.id || ds.name || ds.filename) === deleteSourceId) || {},
      'filename'
    );
    if (deleteConfirmInput.trim() !== filename) {
      setDeleteConfirmError("Filename does not match. Please type the exact filename to confirm deletion.");
      return;
    }
    
    try {
      // Use apiService.deleteDocument instead of AdminService.deleteDataSource
      // This calls the correct endpoint: DELETE /documents/{filename}
      await apiService.deleteDocument(filename);
      fetchDataSources();
      setError(null); // Clear any previous errors
    } catch (err) {
      console.error('Error deleting data source:', err);
      setError('Failed to delete data source and its embeddings. Please try again.');
    } finally {
      handleCloseDeleteDialog();
    }
  };
  
  // Get status icon and color
  const getStatusInfo = (status) => {
    switch (status) {
      case 'indexed':
        return { icon: <CheckCircleIcon fontSize="small" />, color: 'success.main', text: 'Indexed' };
      case 'pending':
        return { icon: <WarningIcon fontSize="small" />, color: 'warning.main', text: 'Pending' };
      case 'error':
        return { icon: <ErrorIcon fontSize="small" />, color: 'error.main', text: 'Error' };
      default:
        return { icon: <CheckCircleIcon fontSize="small" />, color: 'success.main', text: 'Indexed' };
    }
  };
  
  // FIXED: Fetch data when pagination parameters change
  useEffect(() => {
    console.log("useEffect triggered - page:", page, "rowsPerPage:", rowsPerPage, "sortBy:", sortBy, "sortDirection:", sortDirection);
    fetchDataSources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage, sortBy, sortDirection]);

  // FIXED: Separate useEffect for filters - reset page when filters change
  useEffect(() => {
    console.log("Filter changed - sourceType:", sourceType, "searchQuery:", searchQuery);
    setPage(0);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceType, searchQuery]);
  
  // Get the value from a source based on the field mapping
  const getFieldValue = (source, field) => {
    const mappedField = fieldMapping[field] || field;
    
    // Try to get value from mapped field first
    if (source[mappedField] !== undefined) {
      // For filenames, strip the ./userfile/ prefix if present
      if (field === 'filename') {
        const filename = source[mappedField];
        return filename.replace(/^\.\/userfiles\//, '');
      }
      return source[mappedField];
    }
    
    // Try alternative field names if mapped field isn't found
    if (field === 'filename' && source.name !== undefined) {
      return source.name.replace(/^\.\/userfiles\//, '');
    }
    
    if (field === 'type' && source.fileTypes && Array.isArray(source.fileTypes) && source.fileTypes.length > 0) {
      return source.fileTypes[0];
    } else if (field === 'type' && source.ext !== undefined) {
      return source.ext;
    }
    
    // Enhanced page count detection with multiple fallbacks
    if (field === 'pages') {
      // Try all possible page count field names
      if (source.page_count !== undefined && source.page_count > 0) return source.page_count;
      if (source.pageCount !== undefined && source.pageCount > 0) return source.pageCount;
      if (source.pages !== undefined && source.pages > 0) return source.pages;
      if (source.page !== undefined && source.page > 0) return source.page;
      // Return 0 when there's no page count information instead of 1
      return 0;
    }
    
    if (field === 'chunks') {
      if (source.chunk_count !== undefined) return source.chunk_count;
      if (source.chunkCount !== undefined) return source.chunkCount;
      if (source.documentCount !== undefined) return source.documentCount;
      if (source.chunks !== undefined) return source.chunks;
      return 0;
    }
    
    return source[field] || '';
  };
  
  // Handle opening the upload dialog
  const handleOpenUploadDialog = () => {
    setOpenUploadDialog(true);
  };

  // Handle closing the upload dialog
  const handleCloseUploadDialog = () => {
    setOpenUploadDialog(false);
  };

  // Handle successful upload
  const handleUploadSuccess = () => {
    // Add a small delay to ensure ChromaDB has persisted the data
    setTimeout(() => {
      console.log("Upload successful, refreshing data sources...");
      fetchDataSources();
    }, 1000); // 1 second delay to ensure data is persisted
  };
  
  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5">Data Sources</Typography>
        
        <Button
          variant="contained"
          startIcon={<CloudUploadIcon />}
          color="primary"
          onClick={handleOpenUploadDialog}
        >
          Add Data Source
        </Button>
      </Box>
      
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Filters
        </Typography>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth >
              <InputLabel>Source Type</InputLabel>
              <Select
                value={sourceType}
                onChange={(e) => setSourceType(e.target.value)}
                label="Source Type"
              >
                <MenuItem value="">All Types</MenuItem>
                <MenuItem value="pdf">PDF</MenuItem>
                <MenuItem value="html">HTML</MenuItem>
                <MenuItem value="docx">Word (DOCX)</MenuItem>
                <MenuItem value="txt">Text</MenuItem>
                <MenuItem value="chm">CHM</MenuItem>
                <MenuItem value="md">Markdown</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          
          <Grid item xs={12} sm={6} md={5}>
            <TextField
              fullWidth
              label="Search by filename"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              variant="outlined"
              margin="dense"
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={4}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleApplyFilters}
                sx={{ flexGrow: 1 }}
              >
                Apply
              </Button>
              
              <Button
                variant="outlined"
                onClick={handleClearFilters}
              >
                Clear
              </Button>
              
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleRefresh}
                disabled={refreshing}
              >
                Refresh
              </Button>
            </Box>
          </Grid>
        </Grid>
      </Paper>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}
      
      {loading && !refreshing ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Paper>
          {refreshing && (
            <LinearProgress sx={{ borderTopLeftRadius: 4, borderTopRightRadius: 4 }} />
          )}
          
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell> {/* For expand/collapse control */}
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('name')}
                    >
                      Filename {/* Using field mapping: filename */}
                      {sortBy === 'name' && (
                        <span style={{ marginLeft: 4 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('type')}
                    >
                      Type {/* Using field mapping: type */}
                      {sortBy === 'type' && (
                        <span style={{ marginLeft: 4 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('page_count')}
                    >
                      Pages {/* Using field mapping: pages */}
                      {sortBy === 'page_count' && (
                        <span style={{ marginLeft: 4 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('chunk_count')}
                    >
                      Chunks {/* Using field mapping: chunks */}
                      {sortBy === 'chunk_count' && (
                        <span style={{ marginLeft: 4 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box 
                      sx={{ 
                        display: 'flex', 
                        alignItems: 'center',
                        cursor: 'pointer'
                      }}
                      onClick={() => handleSort('lastUpdated')}
                    >
                      Last Updated
                      {sortBy === 'lastUpdated' && (
                        <span style={{ marginLeft: 4 }}>
                          {sortDirection === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {dataSources.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No data sources found
                    </TableCell>
                  </TableRow>
                ) : (
                  dataSources.map((source) => {
                    const sourceId = source.id || source.name || source.filename;
                    const isExpanded = expandedRows[sourceId] || false;
                    const statusInfo = getStatusInfo(source.status || 'indexed');
                    
                    return (
                      <React.Fragment key={sourceId}>
                        <TableRow hover>
                          <TableCell padding="checkbox">
                            <IconButton
                              aria-label="expand row"
                              size="small"
                              onClick={() => toggleRowExpansion(sourceId)}
                            >
                              {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                            </IconButton>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                              {getFieldValue(source, 'filename')}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(getFieldValue(source, 'type') || '').toUpperCase()}
                              size="small"
                              color="primary"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{getFieldValue(source, 'pages') || 0}</TableCell>
                          <TableCell>{getFieldValue(source, 'chunks') || 0}</TableCell>
                          <TableCell>
                            {source.last_updated ? (
                              <Tooltip title={new Date(source.last_updated).toLocaleString()}>
                                <Typography variant="body2">
                                  {formatDistanceToNow(new Date(source.last_updated), { addSuffix: true })}
                                </Typography>
                              </Tooltip>
                            ) : (
                              <Tooltip title={source.lastUpdated ? new Date(source.lastUpdated).toLocaleString() : ''}>
                                <Typography variant="body2">
                                  {source.lastUpdated ? formatDistanceToNow(new Date(source.lastUpdated), { addSuffix: true }) : ''}
                                </Typography>
                              </Tooltip>
                            )}
                          </TableCell>
                          <TableCell>
                            {Array.isArray(source.tags) && source.tags.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, maxWidth: 200 }}>
                                {source.tags.slice(0, 3).map((tag, index) => (
                                  <Chip 
                                    key={index}
                                    label={tag}
                                    size="small"
                                    variant="outlined"
                                    sx={{ maxWidth: 80 }}
                                  />
                                ))}
                                {source.tags.length > 3 && (
                                  <Chip
                                    label={`+${source.tags.length - 3}`}
                                    size="small"
                                    variant="outlined"
                                  />
                                )}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.secondary">No tags</Typography>
                            )}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                              <Tooltip title="View Details">
                                <IconButton size="small" onClick={() => toggleRowExpansion(sourceId)}>
                                  <VisibilityIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Delete Source">
                                <IconButton 
                                  size="small" 
                                  color="error" 
                                  onClick={() => handleOpenDeleteDialog(sourceId)}
                                >
                                  <DeleteIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </Box>
                          </TableCell>
                        </TableRow>
                        
                        {/* Expandable row for details */}
                        <TableRow>
                          <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={8}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ margin: 1, padding: 2, backgroundColor: 'rgba(0, 0, 0, 0.02)', borderRadius: 1 }}>
                                <Grid container spacing={2}>
                                  <Grid item xs={12}>
                                    <Typography variant="h6" gutterBottom component="div">
                                      Source Details
                                    </Typography>
                                  </Grid>
                                  
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Source Path
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      {source.name || 'Unknown'}
                                    </Typography>
                                    
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                                      Status
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                      <Box component="span" sx={{ color: statusInfo.color, mr: 0.5, display: 'flex' }}>
                                        {statusInfo.icon}
                                      </Box>
                                      <Typography variant="body2">
                                        {statusInfo.text}
                                      </Typography>
                                    </Box>
                                  </Grid>
                                  
                                  <Grid item xs={12} md={6}>
                                    <Typography variant="subtitle2" color="text.secondary">
                                      Last Updated
                                    </Typography>
                                    <Typography variant="body2" gutterBottom>
                                      {new Date(source.last_updated || source.lastUpdated || '').toLocaleString()}
                                    </Typography>
                                    
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
                                      Description
                                    </Typography>
                                    <Typography variant="body2">
                                      {source.description || 'No description available'}
                                    </Typography>
                                  </Grid>
                                  
                                  {source.error_message && (
                                    <Grid item xs={12}>
                                      <Alert severity="error" sx={{ mt: 1 }}>
                                        {source.error_message}
                                      </Alert>
                                    </Grid>
                                  )}
                                  
                                  <Grid item xs={12}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 1 }}>
                                      All Tags
                                    </Typography>
                                    {Array.isArray(source.tags) && source.tags.length > 0 ? (
                                      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                                        {source.tags.map((tag, index) => (
                                          <Chip 
                                            key={index}
                                            label={tag}
                                            size="small"
                                            variant="outlined"
                                          />
                                        ))}
                                      </Box>
                                    ) : (
                                      <Typography variant="body2">No tags</Typography>
                                    )}
                                  </Grid>
                                </Grid>
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={totalSources}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Paper>
      )}
      
      {/* File Upload Dialog */}
      <DataSourceUploadDialog 
        open={openUploadDialog}
        onClose={handleCloseUploadDialog}
        onSuccess={handleUploadSuccess}
      />
      
      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={handleCloseDeleteDialog}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
      >
        <DialogTitle id="delete-dialog-title">
          Confirm Deletion
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="delete-dialog-description">
            Are you sure you want to delete this data source? This action cannot be undone and all indexed documents from this source will be removed from the search index.<br /><br />
            <b>To confirm, please type the filename below:</b>
          </DialogContentText>
          <Box sx={{ mt: 2 }}>
            <TextField
              autoFocus
              fullWidth
              label="Type filename to confirm"
              value={deleteConfirmInput}
              onChange={e => {
                setDeleteConfirmInput(e.target.value);
                setDeleteConfirmError("");
              }}
              error={!!deleteConfirmError}
              helperText={deleteConfirmError || `Filename: ${getFieldValue(dataSources.find(ds => (ds.id || ds.name || ds.filename) === deleteSourceId) || {}, 'filename')}`}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDeleteDialog}>Cancel</Button>
          <Button
            onClick={handleConfirmDelete}
            color="error"
            autoFocus
            disabled={
              !deleteConfirmInput ||
              deleteConfirmInput !== getFieldValue(dataSources.find(ds => (ds.id || ds.name || ds.filename) === deleteSourceId) || {}, 'filename')
            }
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataSources;